import { useState, useEffect } from 'react';
import CustomInputLabel from 'components/shared/CustomInputLabel';
import { MenuItem, Select, TextField } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers-pro';
import makeStyles from '@mui/styles/makeStyles';
import { browserTimeZone } from 'constants/timeConstants';
import { getTimeZoneAbbreviation } from 'utils/timeUtils';
import { flightTypeOptions } from 'constants/notifyAndFlyConstants';
import { RECAPTCHA_SITE_KEY } from 'constants/envVars';
import ReCAPTCHA from 'react-google-recaptcha';
import { round } from 'lodash-es';
import dayjs from 'utils/customDayJS';

const styles = {
	main: {
		padding: 40,
		height: '100%',
		display: 'flex',
		flexDirection: 'column'
	},
	field: {
		marginBottom: '20px'
		// width: '150px'
	},
	columnsContainer: {
		display: 'flex',
		gridGap: '0 40px'
	},
	column: {
		display: 'flex',
		flexDirection: 'column',
		width: 'calc(50% - 20px)'
	},
	recaptcha: {
		display: 'flex',
		justifyContent: 'center',
		marginTop: 50
	}
};

const NotifyForm = ({
	formFields,
	setFormFields,
	setTime,
	recaptchaLoaded,
	setRecaptchaLoaded,
	setRecaptchaValue,
	radiusObject,
	handleChangeRadius
}) => {
	const useStyles = makeStyles(styles);
	const classes = useStyles();
	const { type, ceiling, start_time, duration } = formFields;
	const currentTime = dayjs(start_time).isBefore();
	const [radiusError, setRadiusError] = useState('');

	const handleChangeRecaptcha = val => {
		setRecaptchaValue(val);
	};

	const handleScriptLoad = () => {
		setRecaptchaLoaded(true);
	};

	const handleInput = e => {
		const val = e.target.value;
		const name = e.target.name;
		setFormFields({
			...formFields,
			[name]: val
		});
	};

	const handleChangeTime = time => {
		setTime(time);
	};

	const CustomDigitalClockSectionItem = ({ children, ...props }) => {
		return (
			<MenuItem {...props} sx={{ padding: '6px' }}>
				{children}
			</MenuItem>
		);
	};

	useEffect(() => {
		if (radiusObject > 8000) {
			setRadiusError('The radius may not be greater than 8,000 ft.');
		} else if (radiusObject < 600) {
			setRadiusError('The radius may not be less than 600 ft.');
		} else {
			setRadiusError('');
		}
	}, [radiusObject]);

	return (
		<div className={classes.main}>
			{recaptchaLoaded && (
				<>
					<div className={classes.field}>
						<CustomInputLabel text={'Select Type of Flight'} />
						<Select
							fullWidth
							displayEmpty
							name='type'
							value={type}
							onChange={handleInput}
							variant='standard'
						>
							{flightTypeOptions.map((option, key) => (
								<MenuItem key={key} value={option.value}>
									{option.label}
								</MenuItem>
							))}
						</Select>
					</div>
					<div className={classes.columnsContainer}>
						<div className={classes.column}>
							<div className={classes.field}>
								<CustomInputLabel text={'Ceiling (feet)'} />
								<TextField
									fullWidth
									type='number'
									name='ceiling'
									value={ceiling}
									onChange={handleInput}
									variant='standard'
									InputProps={{ inputProps: { min: 0 } }}
									error={ceiling < 25 || ceiling > 400}
									helperText={
										ceiling < 25
											? 'Ceiling may not be less than 25.'
											: ceiling > 400
												? 'Ceiling may not be greater than 400.'
												: ''
									}
								/>
							</div>
							<div className={classes.field}>
								<CustomInputLabel
									text={`Start Time (${getTimeZoneAbbreviation(browserTimeZone)})`}
								/>
								<TimePicker
									variant='standard'
									value={start_time}
									onChange={handleChangeTime}
									slots={{
										textField: TextField,
										digitalClockSectionItem: CustomDigitalClockSectionItem
									}}
									slotProps={{
										textField: textFieldProps => ({
											variant: 'standard',
											error: currentTime,
											helperText: currentTime && 'Start time may not be in the past.'
										})
									}}
								/>
							</div>
						</div>

						<div className={classes.column}>
							<div className={classes.field}>
								<CustomInputLabel text={'Duration (minutes)'} />
								<TextField
									fullWidth
									type='number'
									name='duration'
									value={duration}
									onChange={handleInput}
									variant='standard'
									InputProps={{ inputProps: { min: 0 } }}
									error={duration < 1 || duration > 60}
									helperText={
										duration < 1
											? 'Duration may not be less than 1.'
											: duration > 60
												? 'Duration may not be greater than 60.'
												: ''
									}
									// InputProps={{
									// 	endAdornment: <InputAdornment position='end'>minutes</InputAdornment>
									// }}
								/>
							</div>
							<div className={classes.field}>
								<CustomInputLabel text={'RADIUS (FT)'} />
								<TextField
									fullWidth
									type='number'
									InputProps={{ inputProps: { min: 0 } }}
									value={round(radiusObject, 2)}
									onChange={handleChangeRadius}
									variant='standard'
									error={radiusObject > 8000 || radiusObject < 600}
									helperText={radiusError}
								/>
							</div>
						</div>
					</div>
				</>
			)}
			<div className={classes.recaptcha}>
				<ReCAPTCHA
					sitekey={RECAPTCHA_SITE_KEY}
					onChange={handleChangeRecaptcha}
					asyncScriptOnLoad={handleScriptLoad}
				/>
			</div>
		</div>
	);
};

export default NotifyForm;
